import { Injectable } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup, Validators } from "@angular/forms";

import { DffFormControlType, DffOprindeligAktuelVaerdiFormGroup, DffReactiveFormControlValueMapper as Map } from "@dffedb/forms";
import { DffOprindeligAktuelVaerdi } from "@dffedb/util";
import { Huskeliste } from "@e-forsyning/common/huskeliste";
import { MaalerAflaesning, MaalerAflaesningFormBuilder, MaalerAflaesningMedNoter, MaalerType } from "@e-forsyning/common/maaler";
import { NoteBruger, NoteFormBuilder, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus } from "@e-forsyning/common/note";

import { BatteriskiftData } from "./model/batteriskift-data.model";
import { Batteriskift } from "./model/batteriskift.model";

@Injectable({ providedIn: "root" })
export class BatteriskiftFormBuilder {
    public batteriskiftDataToFormGroup(
        data: BatteriskiftData,
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        omraader: NoteOmraade[],
        henvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        brugere: NoteBruger[]
    ): UntypedFormGroup {
        const result = new UntypedFormGroup({
            batteriskift: this.batteriskiftToFormGroup(
                data.batteriskift,
                maalerTyper,
                huskelister,
                omraader,
                henvendelsestyper,
                noteOpgavestyringStatusser,
                brugere
            )
        });
        return result;
    }

    public batteriskiftToFormGroup(
        batteriskift: Batteriskift,
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const maalerFoerBatteriskift = this.maaleraflaesningFoerBatteriskiftToFormGroup(
            batteriskift.maalerFoerBatteriskift,
            maalerTyper,
            noteOmraader,
            noteHenvendelsestyper,
            noteOpgavestyringStatusser,
            noteBrugere
        );
        const maalerEfterBatteriskift = this.maaleraflaesningEfterBatteriskiftToFormGroup(
            batteriskift.maalerEfterBatteriskift,
            maalerTyper
        );
        const result = new UntypedFormGroup({
            dato: new DffOprindeligAktuelVaerdiFormGroup(
                batteriskift.dato,
                { controlType: DffFormControlType.DateInput, placeholder: "Dato for batteriskifte" },
                Validators.required
            ),
            huskelisteId: this.huskelisteIdToFormControl(batteriskift.huskelisteId, huskelister),
            maalerFoerBatteriskift,
            maalerEfterBatteriskift
        });
        this.synkroniserAflaesninger(maalerFoerBatteriskift, maalerEfterBatteriskift);
        return result;
    }

    public formGroupToBatteriskiftData(formGroup: UntypedFormGroup): BatteriskiftData {
        const result: BatteriskiftData = {
            batteriskift: this.formGroupToBatteriskift(formGroup.controls["batteriskift"] as UntypedFormGroup)
        };
        return result;
    }

    public formGroupToBatteriskift(formGroup: UntypedFormGroup): Batteriskift {
        const result: Batteriskift = {
            dato: Map.toOprindeligAktuelDates(formGroup.controls["dato"]),
            maalerFoerBatteriskift: this.formGroupToMaalerAflaesningMedNoter(
                formGroup.controls["maalerFoerBatteriskift"] as UntypedFormGroup
            ),
            huskelisteId: Map.toOprindeligAktuelIntegers(formGroup.controls["huskelisteId"]),
            maalerEfterBatteriskift: this.formGroupToMaalerAflaesning(formGroup.controls["maalerEfterBatteriskift"] as UntypedFormGroup)
        };
        return result;
    }

    private synkroniserAflaesninger(maalerFoerBatteriskift: UntypedFormGroup, maalerEfterBatteriskift: UntypedFormGroup): void {
        const taellevaerkerFoer = maalerFoerBatteriskift.get("taellevaerker") as UntypedFormArray;
        const taellevaerkerEfter = maalerEfterBatteriskift.get("taellevaerker") as UntypedFormArray;

        taellevaerkerFoer.valueChanges.subscribe((x) => {
            const value = taellevaerkerFoer.getRawValue();
            taellevaerkerEfter.patchValue(value);
        });
    }

    private maaleraflaesningFoerBatteriskiftToFormGroup(
        aflaesning: MaalerAflaesningMedNoter,
        maalerTyper: MaalerType[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const maalerType = maalerTyper.find((t) => t.id === aflaesning.maalerTypeId.aktuel) || ({ taellevaerker: [] } as MaalerType);
        const result = MaalerAflaesningFormBuilder.maalerAflaesningToFormGroup(aflaesning, maalerType, true);
        result.setControl(
            "noter",
            NoteFormBuilder.noterToFormArray(aflaesning.noter, noteOmraader, noteHenvendelsestyper, noteOpgavestyringStatusser, noteBrugere)
        );
        return result;
    }

    private maaleraflaesningEfterBatteriskiftToFormGroup(aflaesning: MaalerAflaesning, maalerTyper: MaalerType[]): UntypedFormGroup {
        const maalerType = maalerTyper.find((t) => t.id === aflaesning.maalerTypeId.aktuel) || ({ taellevaerker: [] } as MaalerType);
        const result = MaalerAflaesningFormBuilder.maalerAflaesningToFormGroup(aflaesning, maalerType, false);
        return result;
    }

    private huskelisteIdToFormControl(huskelisteId: DffOprindeligAktuelVaerdi<number>, huskelister: Huskeliste[]): UntypedFormGroup {
        const result = new DffOprindeligAktuelVaerdiFormGroup(huskelisteId, {
            controlType: DffFormControlType.DropdownList,
            dropdownListOptions: [{}, ...huskelister],
            placeholder: "Huskeliste"
        });
        return result;
    }

    private formGroupToMaalerAflaesning(formGroup: UntypedFormGroup): MaalerAflaesning {
        const maalerAflaesning = MaalerAflaesningFormBuilder.formGroupToMaalerAflaesning(formGroup);
        const result: MaalerAflaesning = {
            ...maalerAflaesning
        };
        return result;
    }

    private formGroupToMaalerAflaesningMedNoter(formGroup: UntypedFormGroup): MaalerAflaesningMedNoter {
        const maalerAflaesning = MaalerAflaesningFormBuilder.formGroupToMaalerAflaesning(formGroup);
        const noter = NoteFormBuilder.formArrayToNoter(formGroup.controls["noter"] as UntypedFormArray);
        const result: MaalerAflaesningMedNoter = {
            ...maalerAflaesning,
            noter
        };
        return result;
    }
}
